import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { PhotoCamera } from '../../../../components/PhotoCamera';
import { FileTypes } from '../../../../types/Files/FileTypes';
import { PhotoDimensions } from '../../../../types/Camera/PhotoDimensions';
import { Button } from '../../../../components/buttons/Button';
import { NextSection } from '../../../../components/buttons/NextSection';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { NavForward } from '../../../../components/buttons/NavForward';
import { Uploader } from '../../../../components/Uploader';

import { isMobile } from 'react-device-detect';
import { Heading } from '../../../../components/Heading';

import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/photos/photoForCard')({
  component: PhotoForCard,
});

function PhotoForCard() {
  const { mutate } = useUpdateChartSection();
  const [takingPhoto, setTakingPhoto] = React.useState({
    idFront: false,
    idBack: false,
    insuranceFront: false,
    insuranceBack: false,
    medicareFront: false,
    medicareBack: false,
    portrait: false,
    testDocument: false,
  });
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [warned, setWarned] = React.useState(false);
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);

  // WRITE FORM DATA TO FIRESTORE
  const handleCreateBlank = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          PHOTO_FOR_CARD: {
            portraitURI: '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE EMPTY FIRESTORE DOC IF IT DOESN'T EXIST
  React.useEffect(() => {
    const docRef = chart?.PHOTO_FOR_CARD;
    if (chart && !docRef) {
      handleCreateBlank();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'photoForCard', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'photoForCard',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  const handleIdInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          PHOTO_ID: {
            issuingState: idForm.state.values.issuingState,
            licenseNumber: idForm.state.values.idNumber,

            photoIdentificationFrontURI: chart?.PHOTO_ID?.photoIdentificationFrontURI
              ? chart.PHOTO_ID.photoIdentificationFrontURI
              : '',
            photoIdentificationBackURI: chart?.PHOTO_ID?.photoIdentificationFrontURI
              ? chart.PHOTO_ID.photoIdentificationFrontURI
              : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE FORM
  const idForm = useForm({
    defaultValues: {
      issuingState: chart?.PHOTO_ID?.issuingState ? chart.PHOTO_ID.issuingState : '',
      idNumber: chart?.PHOTO_ID?.licenseNumber ? chart?.PHOTO_ID.licenseNumber : '',
    },
    onSubmit: async () => {
      await handleIdInfoUpdate();
    },
  });

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PHOTO_FOR_CARD) {
    return (
      <>
        {/* <Header patientId={patientId} /> */}
        <div id="photo-upload-page" className="flex flex-col">
          <Heading id="photo-for-card" textContent="Photo for Card" />
          <div className="flex flex-col items-center">
            <p className="text-xl">You will now take a self-portrait.</p>
            <p className="text-xl">This photo will be printed on your MIDEO identity card.</p>
            <p className="text-xl">It should resemble a traditional government ID photo.</p>
          </div>
          <div id="photo-for-card-container" className="my-4">
            <div className="flex flex-col items-center">
              {chart?.PHOTO_FOR_CARD?.portraitURI && takingPhoto.portrait === false ? (
                <div className="flex flex-row items-center justify-center">
                  <img
                    src={chart.PHOTO_FOR_CARD?.portraitURI}
                    style={{ width: `${PhotoDimensions.desktopWidth}%` }}
                    alt="Current Portrait"
                  />
                  <div className="ml-4">
                    <Button
                      id="retake-portrait"
                      type="button"
                      textContent="Retake Photo"
                      onClick={() => setTakingPhoto({ ...takingPhoto, portrait: true })}
                    />
                  </div>
                </div>
              ) : (
                takingPhoto.portrait === false && (
                  <Button
                    id="take-portrait"
                    type="button"
                    textContent="Take Photo"
                    onClick={() => setTakingPhoto({ ...takingPhoto, portrait: true })}
                  />
                )
              )}
              {takingPhoto.portrait && (
                <PhotoCamera
                  type={FileTypes.portrait}
                  docToUpdate="PHOTO_FOR_CARD"
                  style={'portrait'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/portrait`}
                  patientId={patientId}
                  hideCamera={() => {
                    setPhotoOneWorking(false);
                    setTakingPhoto({ ...takingPhoto, portrait: false });
                  }}
                  working={photoOneWorking}
                  setWorking={setPhotoOneWorking}
                />
              )}
            </div>
            {/* ALLOW CONCIERGE PATIENTS TO UPLOAD A PHOTO */}
            {patientInfo.navType.base === 'concierge' && (
              <div className="my-12 flex flex-col items-center">
                <p className="text-xl">Or upload a photo:</p>
                <Uploader
                  docToUpdate="PHOTO_FOR_CARD"
                  bucket="patient"
                  type={FileTypes.portrait}
                  title="Upload Photo"
                  elementId="photo-file-upload"
                />
              </div>
            )}
            <div id="nav" className="mt-4 flex flex-col items-center">
              {/* {prevPage && <NavBack to={prevPage} />} */}
              {patientInfo.navType.base === 'self' && nextPageURL && (
                <NavForward to={nextPageURL} disabled={!chart.PHOTO_FOR_CARD?.portraitURI} />
              )}
              {patientInfo?.conciergeNavigator?.documentsUnlocked && (
                <div className="mt-4 place-self-center">
                  <NextSection to={nextPageURL ? nextPageURL : '../'} />
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
